import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import qrcode from 'qrcode-generator';
import { securityApi, type QrSignInView } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { Alert, Button, TextInput } from './ui.tsx';
import { isFreshAuthRequired, isMfaSetupRequired } from './elevate-dialog.tsx';
import { QR_SIGN_IN_POLL_SECONDS, type QrSignInStatus } from '@shared/qr-sign-in.ts';

/**
 * Settings → Security → "Sign in on your phone".
 *
 * Shows a QR code the mobile app scans, then asks for the code that the
 * scanning phone shows. The rules behind each step are in
 * services/auth/qr-sign-in.ts; what this screen is careful about:
 *
 *   * The code is typed HERE, from the phone. That is the step that stops a
 *     photographed QR code from working for whoever scanned it first, so the
 *     screen never offers to skip it and says plainly what to do when a phone
 *     the person does not recognise turns up.
 *   * Nothing outlives the screen. Leaving it withdraws an attempt that is
 *     still open, so a QR code is never left scannable on a page nobody is
 *     looking at.
 *   * The QR code itself is drawn from the module matrix as an SVG, on white
 *     whatever the theme — scanners need the contrast.
 */

const LIVE_STATUSES: readonly QrSignInStatus[] = ['waiting_for_scan', 'waiting_for_code', 'approved'];

interface Attempt {
  readonly id: string;
  readonly payload: string;
  /** When the code runs out, on THIS browser's clock (see `Countdown`). */
  readonly deadline: number;
}

export default function PhoneSignIn({
  onFreshAuthRequired,
  onSignedIn,
}: {
  /** The freshness gate said no; the caller opens the "confirm it's you" dialog and retries. */
  onFreshAuthRequired: (retry: () => void) => void;
  /** A phone has its session — the caller's session list is out of date. */
  onSignedIn?: () => void;
}) {
  const { t } = useTranslation();
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [askPassword, setAskPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [needsFactor, setNeedsFactor] = useState(false);
  const [denied, setDenied] = useState(false);

  const view = useQuery({
    queryKey: ['qr-sign-in', attempt?.id],
    queryFn: () => securityApi.qrSignIn(attempt!.id),
    enabled: attempt !== null,
    // The poll is its own retry; a failed read is simply asked again next time.
    retry: false,
    refetchInterval: (query) => {
      if (isGone(query.state.error)) return false;
      const status = query.state.data?.status;
      return !status || LIVE_STATUSES.includes(status) ? QR_SIGN_IN_POLL_SECONDS * 1000 : false;
    },
  });
  // An attempt the hourly housekeeping has already deleted reads as 404, and
  // the last answer before it would otherwise stay on screen — a tab left in
  // the background past the deadline would poll "waiting" for ever. Gone is
  // expired.
  const status: QrSignInStatus | null = attempt
    ? isGone(view.error)
      ? 'expired'
      : (view.data?.status ?? 'waiting_for_scan')
    : null;
  const live = status !== null && LIVE_STATUSES.includes(status);

  useEffect(() => {
    if (status === 'signed_in') onSignedIn?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Withdraw an open attempt when the screen goes away. A ref, so the cleanup
  // sees the attempt as it is at unmount rather than as it was when it ran.
  const open = useRef<string | null>(null);
  useEffect(() => {
    open.current = attempt && live ? attempt.id : null;
  }, [attempt, live]);
  useEffect(
    () => () => {
      if (open.current) void securityApi.cancelQrSignIn(open.current).catch(() => undefined);
    },
    [],
  );

  const start = useMutation({
    mutationFn: (withPassword?: string) => securityApi.startQrSignIn(withPassword),
    // Asked afresh every time: a factor enrolled in the section above since the
    // last try is exactly what this was waiting for.
    onMutate: () => setNeedsFactor(false),
    onSuccess: (started) => {
      setAttempt({
        id: started.id,
        payload: started.payload,
        deadline: Date.now() + started.expiresInSeconds * 1000,
      });
      setAskPassword(false);
      setPassword('');
      setCode('');
      setError(null);
      setDenied(false);
    },
    onError: (caught, withPassword) => {
      if (isFreshAuthRequired(caught)) {
        setError(null);
        onFreshAuthRequired(() => start.mutate(withPassword));
        return;
      }
      if (isMfaSetupRequired(caught)) {
        setAttempt(null);
        setNeedsFactor(true);
        return;
      }
      if (caught instanceof ApiRequestError && caught.fields.password) {
        // The first press asks without a password; an account with no second
        // factor is told to type one, and that is not an error yet. Any
        // finished attempt still on screen goes, or the form (which belongs
        // to the no-attempt state) would never appear.
        setAttempt(null);
        setAskPassword(true);
        setError(withPassword ? caught.message : null);
        return;
      }
      setError(caught instanceof ApiRequestError ? caught.message : t('security.qrStartError'));
    },
  });

  const approve = useMutation({
    mutationFn: () => securityApi.approveQrSignIn(attempt!.id, code),
    onSuccess: () => {
      setCode('');
      setError(null);
      void view.refetch();
    },
    onError: (caught) => {
      setError(caught instanceof ApiRequestError ? caught.message : t('security.qrApproveError'));
      void view.refetch();
    },
  });

  const cancel = useMutation({
    mutationFn: () => securityApi.cancelQrSignIn(attempt!.id),
    onSettled: () => void view.refetch(),
  });

  function reset() {
    setAttempt(null);
    setCode('');
    setError(null);
    setDenied(false);
  }

  return (
    <div className="rounded border border-slate-200 p-4">
      <h3 className="font-semibold">{t('security.qrTitle')}</h3>
      <p className="text-sm text-slate-600">{t('security.qrIntro')}</p>

      {error ? (
        <div className="mt-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {needsFactor ? (
        <div className="mt-3">
          <Alert tone="warning">{t('security.qrNeedsFactor')}</Alert>
        </div>
      ) : null}

      {!attempt && askPassword ? (
        <form
          className="mt-3"
          onSubmit={(event) => {
            event.preventDefault();
            start.mutate(password);
          }}
        >
          <TextInput
            label={t('security.qrPasswordLabel')}
            hint={t('security.qrPasswordHint')}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          <Button type="submit" variant="primary" busy={start.isPending} disabled={!password}>
            {t('security.qrShow')}
          </Button>{' '}
          <Button
            variant="ghost"
            onClick={() => {
              setAskPassword(false);
              setPassword('');
              setError(null);
            }}
          >
            {t('common.cancel')}
          </Button>
        </form>
      ) : null}

      {!attempt && !askPassword ? (
        <Button variant="secondary" className="mt-3" busy={start.isPending} onClick={() => start.mutate(undefined)}>
          {t('security.qrShow')}
        </Button>
      ) : null}

      {attempt && status === 'waiting_for_scan' ? (
        <div className="mt-3 space-y-3">
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            <li>{t('security.qrStepOpen')}</li>
            <li>{t('security.qrStepScan')}</li>
            <li>{t('security.qrStepCode')}</li>
          </ol>
          <QrCode value={attempt.payload} label={t('security.qrImageLabel')} />
          <Countdown deadline={view.data ? view.dataUpdatedAt + view.data.expiresInSeconds * 1000 : attempt.deadline} />
          <Button variant="ghost" onClick={() => cancel.mutate()}>
            {t('common.cancel')}
          </Button>
        </div>
      ) : null}

      {attempt && status === 'waiting_for_code' ? (
        <form
          className="mt-3 space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            approve.mutate();
          }}
        >
          <p className="text-sm">{t(platformKey(view.data))}</p>
          <TextInput
            label={t('security.qrCodeLabel')}
            hint={t('security.qrCodeHint')}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            inputMode="numeric"
            autoComplete="off"
            maxLength={7}
            required
          />
          <Countdown deadline={view.data ? view.dataUpdatedAt + view.data.expiresInSeconds * 1000 : attempt.deadline} />
          <Button type="submit" variant="primary" busy={approve.isPending} disabled={code.trim().length < 6}>
            {t('security.qrApprove')}
          </Button>{' '}
          <Button
            variant="ghost"
            onClick={() => {
              setDenied(true);
              cancel.mutate();
            }}
          >
            {t('security.qrNotMine')}
          </Button>
        </form>
      ) : null}

      {attempt && status === 'approved' ? (
        <div className="mt-3">
          <Alert tone="info">{t('security.qrApproved')}</Alert>
        </div>
      ) : null}

      {attempt && status === 'signed_in' ? (
        <div className="mt-3 space-y-3">
          <Alert tone="success">{t('security.qrSignedIn')}</Alert>
          <Button variant="secondary" onClick={reset}>
            {t('common.done')}
          </Button>
        </div>
      ) : null}

      {attempt && (status === 'cancelled' || status === 'expired') ? (
        <div className="mt-3 space-y-3">
          <Alert tone={denied ? 'warning' : 'info'}>
            {denied
              ? t('security.qrDenied')
              : status === 'expired'
                ? t('security.qrExpired')
                : t('security.qrCancelled')}
          </Alert>
          <Button variant="secondary" busy={start.isPending} onClick={() => start.mutate(undefined)}>
            {t('security.qrShowNew')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function isGone(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 404;
}

function platformKey(view: QrSignInView | undefined): string {
  if (view?.platform === 'ios') return 'security.qrClaimedIos';
  if (view?.platform === 'android') return 'security.qrClaimedAndroid';
  return 'security.qrClaimed';
}

/**
 * Seconds left, ticking. The server's status is what actually decides; this is
 * only a hint — measured from when its answer arrived, on this browser's
 * clock, so a clock that disagrees with the server's cannot make it lie.
 */
function Countdown({ deadline }: { deadline: number }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const left = Math.max(0, Math.round((deadline - now) / 1000));
  const time = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
  return <p className="text-sm text-slate-600">{t('security.qrExpiresIn', { time })}</p>;
}

/**
 * A QR code as an SVG drawn from its modules — no markup strings, nothing set
 * through `innerHTML`. Error correction M; the payload is about a hundred
 * ASCII characters, which keeps the code small enough to scan off a laptop
 * screen at arm's length.
 */
export function QrCode({ value, label, size = 224 }: { value: string; label: string; size?: number }) {
  const { path, dimension } = useMemo(() => {
    const code = qrcode(0, 'M');
    code.addData(value, 'Byte');
    code.make();
    const count = code.getModuleCount();
    // The four-module quiet zone the standard asks for.
    const margin = 4;
    let d = '';
    for (let row = 0; row < count; row += 1) {
      for (let column = 0; column < count; column += 1) {
        if (code.isDark(row, column)) d += `M${column + margin} ${row + margin}h1v1h-1z`;
      }
    }
    return { path: d, dimension: count + margin * 2 };
  }, [value]);

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${dimension} ${dimension}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
      className="rounded border border-slate-200"
    >
      <rect width={dimension} height={dimension} fill="#ffffff" />
      <path d={path} fill="#000000" />
    </svg>
  );
}
